import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';

import { cn } from '../../lib/utils';
import { TSizes, TColors } from '../../types';

type TButtonVariant = 'filled' | 'outline' | 'ghost' | 'text';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-button [font-weight:var(--font-button-weight)] [letter-spacing:var(--font-button-letter-spacing)] transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        filled:
          'rounded-md bg-primary text-primary-foreground hover:opacity-90',
        outline:
          'rounded-md bg-transparent border-2 border-solid border-primary text-primary hover:bg-primary/10',
        ghost: 'rounded-md bg-transparent text-primary hover:bg-primary/10',
        text: 'font-sans bg-transparent text-current hover:opacity-70 h-auto p-0 min-h-0',
      } as const,
      size: {
        s: 'h-[38px] px-3 text-xs',
        m: 'h-[51px] px-4 text-sm',
        l: 'h-[60px] px-5 text-base',
      } as const,
      color: {
        primary: '',
        accent: '',
        success: '',
        error: '',
        warning: '',
        orange: '',
        info: '',
        contrast: '',
        secondary: '',
        text: '',
        note: '',
        noteContrast: '',
        secondaryContrast: '',
        lavender: '',
      } as const,
    },
    compoundVariants: [
      // Filled variant colors
      {
        variant: 'filled',
        color: 'accent',
        class: 'bg-accent text-accent-foreground',
      },
      {
        variant: 'filled',
        color: 'success',
        class: 'bg-success text-success-foreground',
      },
      {
        variant: 'filled',
        color: 'error',
        class: 'bg-destructive text-destructive-foreground',
      },
      {
        variant: 'filled',
        color: 'warning',
        class: 'bg-warning text-warning-foreground',
      },
      {
        variant: 'filled',
        color: 'orange',
        class: 'bg-orange text-orange-foreground',
      },
      {
        variant: 'filled',
        color: 'info',
        class: 'bg-info text-info-foreground',
      },
      {
        variant: 'filled',
        color: 'contrast',
        class: 'bg-contrast-foreground text-contrast',
      },
      {
        variant: 'filled',
        color: 'secondary',
        class: 'bg-secondary text-secondary-foreground',
      },
      {
        variant: 'filled',
        color: 'lavender',
        class: 'bg-lavender text-black',
      },
      // Outline variant colors
      {
        variant: 'outline',
        color: 'primary',
        class: 'border-primary text-primary hover:bg-primary/10',
      },
      {
        variant: 'outline',
        color: 'accent',
        class: 'border-accent text-accent hover:bg-accent/10',
      },
      {
        variant: 'outline',
        color: 'success',
        class: 'border-success text-success hover:bg-success/10',
      },
      {
        variant: 'outline',
        color: 'error',
        class: 'border-destructive text-destructive hover:bg-destructive/10',
      },
      {
        variant: 'outline',
        color: 'warning',
        class: 'border-warning text-warning hover:bg-warning/10',
      },
      {
        variant: 'outline',
        color: 'orange',
        class: 'border-orange text-orange hover:bg-orange/10',
      },
      {
        variant: 'outline',
        color: 'info',
        class: 'border-info text-info hover:bg-info/10',
      },
      {
        variant: 'outline',
        color: 'contrast',
        class: 'border-contrast text-contrast hover:bg-contrast/10',
      },
      {
        variant: 'outline',
        color: 'secondary',
        class: 'border-secondary text-secondary hover:bg-secondary/10',
      },
      {
        variant: 'outline',
        color: 'lavender',
        class: 'border-lavender text-lavender hover:bg-lavender/10',
      },
      // Ghost variant colors
      {
        variant: 'ghost',
        color: 'accent',
        class: 'text-accent hover:bg-accent/10',
      },
      {
        variant: 'ghost',
        color: 'success',
        class: 'text-success hover:bg-success/10',
      },
      {
        variant: 'ghost',
        color: 'error',
        class: 'text-destructive hover:bg-destructive/10',
      },
      {
        variant: 'ghost',
        color: 'warning',
        class: 'text-warning hover:bg-warning/10',
      },
      {
        variant: 'ghost',
        color: 'contrast',
        class: 'text-contrast hover:bg-contrast/10',
      },
      {
        variant: 'ghost',
        color: 'lavender',
        class: 'text-lavender hover:bg-lavender/10',
      },
      // Text variant sizes
      { variant: 'text', size: 's', class: 'h-auto px-0 text-xs' },
      { variant: 'text', size: 'm', class: 'h-auto px-0 text-sm' },
    ],
    defaultVariants: {
      variant: 'filled',
      size: 'm',
      color: 'primary',
    },
  },
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: TButtonVariant;
  size?: TSizes;
  color?: TColors;
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, color, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, color, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export type { TButtonVariant };
export { Button, buttonVariants };
